import type { Route } from '@application/types';
import { useRouter } from '@application/utils';
import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { useTheme } from '@emotion/react';
import { Avatar, Container, Label } from '..';
import { LanguageSelector } from '../Header/components/LanguageSelector';
import { Logo } from '../Logo';
import { Alert } from './components/Alert';
import { MobileHeader } from './components/MobileHeader';
import { Navigation } from './components/Navigation';
import * as Styled from './styled';
import type { MtLayoutProps } from './types';

export {
  MtLayouTitleEditButton,
  MtLayouTitleEditButtonContainer,
  MtLayoutTitleWrapper,
} from './styled';
export * from './types';

export const WRAPPER_NO_PADDING: Route[] = ['mtMessages', 'mtPuppyGuide'];

export const MT_LAYOUT_CONTENT_WRAPPER_ID = 'mt-layout-content-wrapper';

export const BREEDER_PROFILE_TOTAL_STEPS = 5;

export const MtLayout: FC<MtLayoutProps> = (props) => {
  const {
    navbarLinkCategories,
    dropdownLinks,
    onHomeClick,
    onLanguageChange,
    onLogoutClick,
    children,
    alertBar,
    user,
    unfinishedApplicationComponent,
    notificationsPanel,
    profileCurrentProgress,
    languageSelectorButtons,
    ...rest
  } = props;

  const { translate } = useTranslation();
  const { infoFromCurrentPath } = useRouter();
  const progress = profileCurrentProgress
    ? Math.floor((profileCurrentProgress / BREEDER_PROFILE_TOTAL_STEPS) * 100)
    : undefined;

  const theme = useTheme();
  return (
    <Styled.Wrapper {...rest}>
      <Styled.SideWrapper>
        <Styled.SideLogoWrapper onClick={onHomeClick}>
          <Logo type="tipaw" color="dark" />
        </Styled.SideLogoWrapper>
        <Styled.SideNavigationWrapper>
          <Navigation navbarLinkCategories={navbarLinkCategories} onLogoutClick={onLogoutClick} />
        </Styled.SideNavigationWrapper>
      </Styled.SideWrapper>
      <Styled.Header>
        <Styled.HeaderWrapper>
          <Styled.HeaderTitleWrapper>
            <Styled.HeaderLogoWrapper onClick={onHomeClick}>
              <Logo type="tipaw" color="dark" />
            </Styled.HeaderLogoWrapper>
            <Styled.HeaderTitle>{translate('mt.header.title')}</Styled.HeaderTitle>
          </Styled.HeaderTitleWrapper>
          <Styled.HeaderRightContainer>
            <LanguageSelector languageSelectorButtons={languageSelectorButtons} />
            {notificationsPanel}
            <Styled.HeaderProfileWrapper>
              <Styled.HeaderProfileContent>
                {!!user.image && (
                  <Styled.HeaderProfileImage>
                    <Avatar src={user.image ?? PLACEHOLDER_IMAGE_USER} />
                  </Styled.HeaderProfileImage>
                )}

                {user.name}

                {progress && progress !== 100 && (
                  <Styled.HeaderDropdownProgressWrapper>
                    <Label
                      content={`${progress}%`}
                      bgColor={theme.color.blue.lightBlue}
                      textColor={theme.color.blue.default}
                    />
                  </Styled.HeaderDropdownProgressWrapper>
                )}

                <Styled.HeaderDropdownIconWrapper>
                  <FaChevronDown />
                </Styled.HeaderDropdownIconWrapper>
              </Styled.HeaderProfileContent>
              <Styled.HeaderDropdownContent data-dropdown-content>
                {dropdownLinks.map((link, index) => (
                  <Styled.HeaderDropdownLink
                    key={index}
                    onClick={link.onClick}
                    disabled={(link.disabled ?? false).toString()}
                  >
                    <Styled.HeaderDropdownIcon icon={link.icon} /> {link.label}
                  </Styled.HeaderDropdownLink>
                ))}
              </Styled.HeaderDropdownContent>
            </Styled.HeaderProfileWrapper>
            {unfinishedApplicationComponent}
          </Styled.HeaderRightContainer>
          <MobileHeader {...props} />
        </Styled.HeaderWrapper>
      </Styled.Header>
      <Styled.ContentWrapper
        id={MT_LAYOUT_CONTENT_WRAPPER_ID}
        noPadding={WRAPPER_NO_PADDING.includes(infoFromCurrentPath.route)}
      >
        {!!alertBar && (
          <Container>
            <Alert
              {...alertBar}
              profileCurrentProgress={profileCurrentProgress}
              progress={progress}
            />
          </Container>
        )}

        {children}
      </Styled.ContentWrapper>
    </Styled.Wrapper>
  );
};
